import React, { useState, useEffect } from 'react';
import { useVCOnboarding } from '../../../contexts/VCOnboardingContext';
import {
  VCIntegrations,
  IntegrationCategory,
  INTEGRATION_CATEGORIES,
} from '../../../types/vcOnboarding';
import styles from '../VCOnboarding.module.css';

const Step3Integrations: React.FC = () => {
  const { data, saveStep, nextStep, previousStep, loading } = useVCOnboarding();

  const [formData, setFormData] = useState<VCIntegrations>({
    selectedCategories: [],
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data.step3_integrations) {
      setFormData(data.step3_integrations);
    }
  }, [data.step3_integrations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      await saveStep(3, formData);
      nextStep();
    } catch (error) {
      console.error('Error saving step 3:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = (category: IntegrationCategory) => {
    setFormData(prev => ({
      selectedCategories: prev.selectedCategories.includes(category)
        ? prev.selectedCategories.filter(c => c !== category)
        : [...prev.selectedCategories, category],
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Required Integration Categories</h2>
        <p className={styles.cardDescription}>
          Select which types of integrations your portfolio companies should connect.
          They will be able to choose any tool within the selected categories.
        </p>

        <div className={styles.integrationCard} style={{ marginBottom: '1.5rem', background: '#1e1b4b', borderColor: '#5b21b6' }}>
          <p style={{ fontSize: '0.875rem', color: '#c4b5fd', margin: 0 }}>
            {formData.selectedCategories.length} categor{formData.selectedCategories.length !== 1 ? 'ies' : 'y'} selected — portfolio companies will connect at least one tool from each selected category
          </p>
        </div>

        <div className={styles.categoryGrid}>
          {INTEGRATION_CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`${styles.categoryCard} ${
                formData.selectedCategories.includes(category.id) ? styles.selected : ''
              }`}
              onClick={() => toggleCategory(category.id)}
              disabled={isSaving || loading}
            >
              <div className={styles.categoryCardContent}>
                <div className={styles.categoryCardIcon}>{category.icon}</div>
                <div className={styles.categoryCardInfo}>
                  <div className={styles.categoryCardTitle}>{category.title}</div>
                  <div className={styles.categoryCardDescription}>{category.description}</div>
                </div>
              </div>
              <div className={styles.integrationCheckbox}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={previousStep}
            disabled={isSaving || loading}
          >
            <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <span className={styles.loadingSpinner} />
                Saving...
              </>
            ) : (
              <>
                Continue
                <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step3Integrations;
